// React and Redux
import {withRequiredAuthInfo} from '@propelauth/react';
import {decode} from 'html-entities';
import parse from 'html-react-parser';
import {useFlags, withLDConsumer} from 'launchdarkly-react-client-sdk';
// Third Party Libraries
import _ from 'lodash';
import {ArrowLeft, Plus, Upload} from 'lucide-react';
import {
  bindHover,
  bindMenu,
  bindPopper,
  bindTrigger,
} from 'material-ui-popup-state';
import {usePopupState} from 'material-ui-popup-state/hooks';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import PropTypes from 'prop-types';
import React, {memo, useEffect, useMemo, useRef, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {Link, useLocation, useNavigate} from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
// Material UI Icons
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import LoadingButton from '@mui/lab/LoadingButton';
// Material UI Components
import {
  Box,
  Breadcrumbs,
  Button,
  Link as MuiLink,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
// Material UI Utils
import {visuallyHidden} from '@mui/utils';

import {APP_HEADER_HEIGHT, FOOTER_HEIGHT, MAX_PAGE_CONTENT_WIDTH} from '../App';
import DocumentIcon from '../assets/file-text.svg';
import FileIcon from '../assets/file.svg';
import CommonPageWrapper from '../components/CommonPageWrapper';
import {CreateDocumentModal} from '../components/documents/CreateDocument';
import DocumentStatusChip from '../components/documents/DocumentStatusChip';
import Error from '../components/Error';
import Loading from '../components/Loading';
import MoveFileModal from '../components/report-workspace/DocumentFolder/moveFileModal';
import SearchBar from '../components/report-workspace/shared/searchBar';
import TimeAgo from '../components/TimeAgo';
import {
  CREATE_FLOWS,
  DOCUMENT_INGESTION_TYPES,
  DOCUMENT_TYPES,
  FOLDER_PURPOSES,
  INGESTION_STATUSES,
} from '../constants';
// Local Imports
import {setDocument, setReadOnly} from '../data/documents';
import PdfReviewAssistantIcon from '../assets/pdf-review-doc.svg';
import PdfReviewAssistantGreenIcon from '../assets/pdf-review-doc-green.svg';
import {
  useCreateSupportingDocumentsDownloadUrlMutation,
  useDeleteDocumentMutation,
  useGetDocumentsByFolderIdQuery,
  useGetDocumentsQuery,
  useGetFolderQuery,
  useGetFoldersQuery,
  useUpdateDocumentMutation,
} from '../data/documentWorkspaceApi';
import {setSelectedFolder} from '../data/folders';
import {selectEffectiveOrg} from '../data/session';
import {BLACK_100, DARK_TEXT_COLOR, GREEN_BUTTON, WHITE_100} from '../theme';
import Notification from '../components/common/Notification';
import CopyDocumentModal from '../components/documents/CopyDocumentModal';
import {sortRecords, trackProductEvent, triggerDownload} from '../util';
import {initCollabSocket} from '../util/collabSocketManager';
import {getBaseUserProperties} from '../util/tracking';
import AppPage from './AppPage';

const TABLE_HEADERS = [
  {
    label: 'Name',
    id: 'name',
    width: 'auto',
    maxWidth: '30.25rem',
  },
  {
    label: 'File type',
    id: 'type',
    width: '12.5rem',
  },
  {
    label: 'Status',
    id: 'status',
    width: '12.5rem',
  },
  {
    label: 'Last modified',
    id: 'updatedAt',
    width: '16.75rem',
  },
];

const styles = {
  createButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '0.75rem',
    paddingRight: '1rem',
    paddingVertical: '0.625rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: '#26A993',
    },
  },
  createButtonLabel: {
    fontSize: '0.875rem',
    lineHeight: '140%',
    fontWeight: '500',
  },
  cancelButton: {
    borderRadius: '0.5rem',
    paddingVertical: '0.6825rem',
    paddingHorizontal: '1rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: WHITE_100,
    },
  },
  cancelButtonLabel: {
    color: '#043B34',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '130%',
  },
  recentDocuments: {
    label: {
      color: '#043B34',
      fontSize: '1rem',
      fontWeight: '600',
      lineHeight: '120%',
    },
  },
  table: {
    container: {
      background: 'transparent',
    },
    header: {
      cell: {
        height: '2.0625rem',
        backgroundColor: '#E4EAE7',
        padding: '0.5rem 1rem',
        alignItems: 'center',
        marginBottom: '0.75rem',
      },
      label: {
        color: 'rgba(4, 59, 52, 0.70)',
        fontSize: '0.75rem',
        fontWeight: '600',
        lineHeight: '140%',
      },
    },
    row: {
      label: {
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '140%',
        color: '#043B34',
      },
      secondaryLabel: {
        fontSize: '0.75rem',
        fontWeight: '400',
        lineHeight: '140%',
        color: '#043B34',
        opacity: 0.6,
      },
      editable: {
        '&:hover': {
          textDecoration: 'underline dotted',
        },
      },
      inlineTextField: {
        '.MuiInputBase-root': {
          marginBottom: '-4px',
        },
      },
    },
  },
  menu: {
    base: {
      marginTop: '0.75rem',
      borderRadius: '0.25rem',
      border: '1px #E2E5E7',
      boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
      '.MuiMenu-list': {
        padding: '0.5rem',
      },
      '.MuiMenuItem-root': {
        width: '100%',
      },
      '.MuiList-root': {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      },
    },
    popover: {
      borderRadius: '0.25rem',
      border: '1px #E2E5E7',
      boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
      '.MuiPaper-root': {
        paddingY: '1rem',
        paddingX: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        marginLeft: '0.875rem',
        marginTop: '-1rem',
      },
    },
    quickBase: {
      marginTop: '0.25rem',
      borderRadius: '0.25rem',
      border: '1px #E2E5E7',
      boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
    },
    listText: {
      primary: {
        color: DARK_TEXT_COLOR,
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '1.25rem',
        marginLeft: '0.25rem',
      },
      danger: {
        color: '#EC4B27',
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '1.25rem',
        marginLeft: '0.25rem',
      },
      title: {
        color: '#7E8986',
        fontSize: '0.6875rem',
        fontWeight: '600',
        lineHeight: '140%',
        textTransform: 'uppercase',
      },
    },
    item: {
      borderRadius: '0.25rem',
      width: '12.25rem',
      padding: '0.5rem',
      '.MuiListItemIcon-root': {
        minWidth: '2rem',
      },
    },
    quickItem: {
      borderRadius: '0.25rem',
      '.MuiListItemIcon-root': {
        minWidth: '2rem',
      },
    },
  },
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '700',
      lineHeight: '120%',
      color: '#043B34',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#DDe0DE',
    },
    label: {
      fontSize: '0.8125rem',
      fontWeight: '500',
      lineHeight: '150%',
      color: '#043B34',
    },
    heavy: {
      fontSize: '0.8125rem',
      fontWeight: '700',
      lineHeight: '150%',
      color: '#043B34',
    },
  },
  createReport: {
    header: {
      fontSize: '1.625rem',
      fontWeight: '700',
      lineHeight: '120%',
      color: DARK_TEXT_COLOR,
      textAlign: 'center',
    },
    secondaryLabel: {
      fontSize: '1rem',
      textAlign: 'center',
      fontWeight: '400',
      lineHeight: '160%',
      color: '#67716E',
    },
    button: {
      container: {
        width: '18.375rem',
        paddingY: '0.75rem',
        paddingX: '1rem',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '1rem',
        height: '4rem',
        borderRadius: '0.5rem',
        border: '1px solid #DDE0DE;',
        background: WHITE_100,
      },
      label: {
        color: DARK_TEXT_COLOR,
        fontSize: '0.875rem',
        fontWeight: '600',
        lineHeight: '150%',
      },
      secondaryLabel: {
        color: '#7E8986',
        fontSize: '0.75rem',
        fontWeight: '400',
        lineHeight: '140%',
      },
      icon: {
        container: {
          padding: '0.5rem',
          borderRadius: '0.5rem',
          background: GREEN_BUTTON,
          width: '2rem',
          height: '2rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        image: {
          width: '1rem',
          height: '1rem',
          color: WHITE_100,
        },
      },
    },
  },
};

const StyledTableRow = styled(TableRow)(() => ({
  '& td': {
    backgroundColor: WHITE_100,
    borderTop: '1px solid #DFE5E2',
    borderBottom: '1px solid #DFE5E2',
  },
  '& td:first-of-type': {
    borderLeft: '1px solid #DFE5E2',
    borderRadius: '8px 0 0 8px',
  },
  '& td:last-of-type': {
    borderRight: '1px solid #DFE5E2',
    borderRadius: '0 8px 8px 0',
  },
  '&:hover td': {
    backgroundColor: '#EBFAF1 !important',
  },
}));

const CascadingContext = React.createContext({
  parentPopupState: null,
  rootPopupState: null,
});

const CascadingSubmenu = ({
  children,
  id,
  leftIcon,
  rightIcon,
  listItemText,
  ...props
}) => {
  const {parentPopupState} = React.useContext(CascadingContext);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: id,
    parentPopupState,
  });

  return (
    <MenuItem sx={styles.menu.item} {...props} {...bindHover(popupState)}>
      {leftIcon && <ListItemIcon>{leftIcon}</ListItemIcon>}
      <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
        {listItemText}
      </ListItemText>
      {rightIcon && (
        <ListItemIcon sx={{justifyContent: 'flex-end'}}>
          {rightIcon}
        </ListItemIcon>
      )}
      {!rightIcon && <Box width={'2rem'} />}
      <HoverPopover
        {...bindPopper(popupState)}
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        sx={styles.menu.popover}
      >
        {children}
      </HoverPopover>
    </MenuItem>
  );
};

CascadingSubmenu.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  listItemText: PropTypes.string.isRequired,
};

const CascadingMenu = ({popupState, ...props}) => {
  const {rootPopupState} = React.useContext(CascadingContext);
  const context = React.useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState]
  );

  return (
    <CascadingContext.Provider value={context}>
      <Menu {...props} {...bindMenu(popupState)} />
    </CascadingContext.Provider>
  );
};

CascadingMenu.propTypes = {
  popupState: PropTypes.object.isRequired,
};

const CreateDocumentMenu = ({onClick, numDocuments}) => {
  const readOnly = useSelector((state) => state.documents.readOnly);
  const {documentRollForward, pdfRollForward, uploadWithoutRollforward} =
    useFlags(); // TODO: Need to store the document list in the redux state so it can be accessed here
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'createDocumentMenu',
  });
  const _handleClick = (createFlow) => () => {
    if (
      createFlow === CREATE_FLOWS.rollforward ||
      createFlow === CREATE_FLOWS.annualRollForward
    ) {
      trackProductEvent('Rollforward Initiated', {
        source: 'create_button',
        create_flow: createFlow,
      });
    } else if (
      createFlow === CREATE_FLOWS.pdf ||
      createFlow === CREATE_FLOWS.upload
    ) {
      trackProductEvent('PDF Upload Initiated', {
        source: 'create_button',
        create_flow: createFlow,
      });
    }

    onClick(createFlow);
    popupState.close();
  };

  if (!pdfRollForward) {
    return (
      <div>
        <Button
          variant={'contained'}
          startIcon={<AddIcon />}
          sx={styles.createButton}
          {...bindTrigger(popupState)}
        >
          <Typography style={styles.createButtonLabel}>Create new</Typography>
        </Button>
        <CascadingMenu
          popupState={popupState}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
          sx={styles.menu.base}
          slotProps={{
            paper: {
              sx: {
                boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
                borderRadius: '4px',
              },
            },
          }}
        >
          <CascadingSubmenu
            id={'AnnualReport'}
            leftIcon={<img src={DocumentIcon} alt={''} />}
            listItemText={'Annual report'}
            rightIcon={<ChevronRightIcon size={'small'} />}
          >
            {documentRollForward && (
              <MenuItem
                onClick={_handleClick(CREATE_FLOWS.annualRollForward)}
                sx={styles.menu.item}
              >
                <ListItemText
                  primaryTypographyProps={styles.menu.listText.primary}
                >
                  Roll forward prior report...
                </ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={_handleClick(CREATE_FLOWS.annualBlankDocument)}
              sx={styles.menu.item}
            >
              <ListItemText
                primaryTypographyProps={styles.menu.listText.primary}
              >
                Blank document
              </ListItemText>
            </MenuItem>
          </CascadingSubmenu>
          <MenuItem
            onClick={_handleClick(CREATE_FLOWS.quarterly)}
            sx={styles.menu.item}
          >
            <ListItemIcon>
              <img src={DocumentIcon} alt={''} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Quarterly report
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={_handleClick(CREATE_FLOWS.other)}
            sx={styles.menu.item}
          >
            <ListItemIcon>
              <img src={FileIcon} alt={''} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Basic document
            </ListItemText>
          </MenuItem>
        </CascadingMenu>
      </div>
    );
  }

  return (
    <div>
      {!readOnly && (
        <Button
          variant={'contained'}
          startIcon={<AddIcon />}
          sx={styles.createButton}
          {...bindTrigger(popupState)}
        >
          <Typography style={styles.createButtonLabel}>Create new</Typography>
        </Button>
      )}
      <CascadingMenu
        popupState={popupState}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        sx={styles.menu.base}
        slotProps={{
          paper: {
            sx: {
              boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
              borderRadius: '4px',
            },
          },
        }}
      >
        <MenuItem
          onClick={_handleClick(CREATE_FLOWS.blankDocument)}
          sx={styles.menu.item}
        >
          <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
            New report
          </ListItemText>
        </MenuItem>
        {documentRollForward && (
          <MenuItem
            sx={styles.menu.item}
            disabled={numDocuments === 0}
            onClick={_handleClick(CREATE_FLOWS.rollforward)}
          >
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Rollforward existing report
            </ListItemText>
          </MenuItem>
        )}
        {uploadWithoutRollforward && (
          <MenuItem
            sx={styles.menu.item}
            onClick={_handleClick(CREATE_FLOWS.upload)}
          >
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Upload PDF
            </ListItemText>
          </MenuItem>
        )}
        {documentRollForward && pdfRollForward && (
          <MenuItem
            sx={styles.menu.item}
            onClick={_handleClick(CREATE_FLOWS.pdf)}
          >
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Upload & rollforward PDF
            </ListItemText>
          </MenuItem>
        )}
      </CascadingMenu>
    </div>
  );
};

CreateDocumentMenu.propTypes = {
  onClick: PropTypes.func.isRequired,
  numDocuments: PropTypes.number.isRequired,
};

const DeleteDocumentModal = ({rowData, open, onClose, onSuccess, onError}) => {
  const [loading, setLoading] = useState(false);
  const [deleteDocument, {isSuccess, isError, reset}] =
    useDeleteDocumentMutation();

  useEffect(() => {
    if (isSuccess) {
      onSuccess();
      onClose();
      reset();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      onError();
      reset();
    }
  }, [isError]);

  const _deleteDocument = async () => {
    setLoading(true);
    await deleteDocument({
      documentId: rowData.documentId,
    });
    setLoading(false);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width={'28rem'} minWidth={'28rem'} maxWidth={'28rem'}>
          <Stack direction={'column'} sx={styles.modal.base}>
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              flex={1}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography sx={styles.modal.title}>Delete file</Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider orientation={'horizontal'} sx={styles.modal.divider} />
            <Stack
              direction={'column'}
              paddingTop={'1.25rem'}
              paddingBottom={'1.5rem'}
              paddingX={'1.5rem'}
              spacing={'1rem'}
            >
              <Typography component={'div'} style={styles.modal.label}>
                You&apos;re about to delete the file{' '}
                <Typography component={'span'} style={styles.modal.heavy}>
                  {parse(decode(rowData.name))}
                </Typography>
                .
              </Typography>
              <Typography style={styles.modal.label}>
                This action is permanent and can&apos;t be undone.
              </Typography>
            </Stack>
            <Divider orientation={'horizontal'} sx={styles.modal.divider} />
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              flex={1}
              justifyContent={'flex-end'}
              alignItems={'center'}
              spacing={'0.5rem'}
            >
              <Button
                variant={'outlined'}
                sx={styles.cancelButton}
                onClick={onClose}
              >
                <Typography style={styles.cancelButtonLabel}>Cancel</Typography>
              </Button>
              <LoadingButton
                variant={'contained'}
                sx={{...styles.createButton, width: '9.3125rem'}}
                onClick={_deleteDocument}
                loading={loading}
                loadingIndicator={
                  <Loading
                    loading={loading}
                    loadingProps={{color: BLACK_100}}
                  />
                }
              >
                <Typography style={styles.createButtonLabel}>
                  Delete document
                </Typography>
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

DeleteDocumentModal.propTypes = {
  rowData: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

const QuickActionMenu = ({
  rowData,
  isPdfReviewAssistant,
  reset,
  onResetComplete,
  showDocumentDeleteModal,
  showRenameFile,
  closeMenu,
  oncloseMenuComplete,
  onOpen,
  showRollForwardModal,
  showMoveFile,
  showCopyModal,
}) => {
  const {documentRollForward, documentFolders: documentFoldersEnabled} =
    useFlags();
  const readOnly = useSelector((state) => state.documents.readOnly);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'createDocumentMenu',
  });
  const [
    createSupportingDocumentsDownloadUrl,
    {data, isSuccess, isError, error},
  ] = useCreateSupportingDocumentsDownloadUrlMutation();

  useEffect(() => {
    popupState.close();
    oncloseMenuComplete();
  }, [closeMenu]);

  useEffect(() => {
    if (isSuccess) {
      triggerDownload(
        () => ({signedUrl: data.supportingDocumentsSignedUrl}),
        `${rowData.name} - [Supporting Documents].zip`
      );
      popupState.close();
    } else if (isError) {
      console.error('Error downloading supporting document', {error});
    }
  }, [data, isSuccess, isError, error]);

  const _showRenameFile = () => {
    popupState.close();
    showRenameFile();
  };

  const _onOpen = () => {
    popupState.close();
    // If document is PENDING, determine the correct createFlow based on document type
    if (_.get(rowData, 'status') === 'PENDING') {
      const createFlow =
        rowData.ingestionState.type === 'upload'
          ? CREATE_FLOWS.upload
          : CREATE_FLOWS.rollforward;
      onOpen({
        existingDocumentId: rowData.documentId,
        createFlow,
      });
    } else {
      onOpen();
    }
  };

  const _showRollForwardModal = () => {
    popupState.close();
    showRollForwardModal();
  };

  const _showCopyModal = () => {
    popupState.close();
    showCopyModal();
  };

  const _downloadSupportingDocuments = () => {
    createSupportingDocumentsDownloadUrl({documentId: rowData.documentId});
  };

  return (
    <div
      data-component={'QuickActionMenu'}
      data-file-source={'DocumentPage.jsx'}
    >
      {!readOnly && (
        <IconButton {...bindTrigger(popupState)}>
          <MoreHoriz />
        </IconButton>
      )}
      <Menu
        {...bindMenu(popupState)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        sx={{
          ...styles.menu.quickBase,
          '& .MuiPaper-root': {
            minWidth: '10rem',
            boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
            borderRadius: '4px',
          },
        }}
      >
        <MenuItem sx={styles.menu.quickItem} onClick={_onOpen}>
          <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
            Open
          </ListItemText>
        </MenuItem>
        {/* Download roll-forward Excel files - only for roll-forwarded documents */}
        {rowData?.ingestionState?.type === 'rollforward' && (
          <MenuItem
            sx={styles.menu.quickItem}
            onClick={_downloadSupportingDocuments}
            disabled={
              /* eslint-disable react/prop-types */
              !_.has(rowData, 'supportingDocumentsS3Key') ||
              _.isEmpty(rowData.supportingDocumentsS3Key)
              /* eslint-enable react/prop-types */
            }
          >
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Download roll-forward Excel files
            </ListItemText>
          </MenuItem>
        )}
        {/* Download copied excel files - only for copied documents with new workbook */}
        {rowData?.ingestionState?.type === 'copy' &&
          _.has(rowData, 'supportingDocumentsS3Key') &&
          !_.isEmpty(rowData.supportingDocumentsS3Key) && (
            <MenuItem
              sx={styles.menu.quickItem}
              onClick={_downloadSupportingDocuments}
            >
              <ListItemText
                primaryTypographyProps={styles.menu.listText.primary}
              >
                Download copied excel files
              </ListItemText>
            </MenuItem>
          )}
        {/* Download supporting Excel files - for uploaded documents */}
        {rowData?.ingestionState?.type === 'upload' &&
          _.has(rowData, 'supportingDocumentsS3Key') &&
          !_.isEmpty(rowData.supportingDocumentsS3Key) && (
            <MenuItem
              sx={styles.menu.quickItem}
              onClick={_downloadSupportingDocuments}
            >
              <ListItemText
                primaryTypographyProps={styles.menu.listText.primary}
              >
                Download supporting Excel files
              </ListItemText>
            </MenuItem>
          )}
        {documentRollForward &&
          (rowData.type === DOCUMENT_TYPES.annualReport ||
            rowData.type === DOCUMENT_TYPES.quarterlyReport ||
            rowData.type === DOCUMENT_TYPES.semiAnnualReport) &&
          rowData?.ingestionState?.status !== INGESTION_STATUSES.failed && (
            <MenuItem
              sx={styles.menu.quickItem}
              onClick={_showRollForwardModal}
            >
              <ListItemText
                primaryTypographyProps={styles.menu.listText.primary}
              >
                Roll forward
              </ListItemText>
            </MenuItem>
          )}
        {documentFoldersEnabled && (
          <MenuItem sx={styles.menu.quickItem} onClick={showMoveFile}>
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Move
            </ListItemText>
          </MenuItem>
        )}
        {!isPdfReviewAssistant && (
          <MenuItem sx={styles.menu.quickItem} onClick={_showCopyModal}>
            <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
              Make a copy
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem sx={styles.menu.quickItem} onClick={_showRenameFile}>
          <ListItemText primaryTypographyProps={styles.menu.listText.primary}>
            Rename
          </ListItemText>
        </MenuItem>
        <Divider orientation={'horizontal'} sx={styles.modal.divider} />
        <MenuItem sx={styles.menu.quickItem} onClick={showDocumentDeleteModal}>
          <ListItemText primaryTypographyProps={styles.menu.listText.danger}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

QuickActionMenu.propTypes = {
  isPdfReviewAssistant: PropTypes.bool,
  rowData: PropTypes.shape({
    status: PropTypes.string.isRequired,
    isFinal: PropTypes.bool,
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    ingestionState: PropTypes.shape({
      status: PropTypes.string,
      type: PropTypes.string,
      source: PropTypes.string,
      skipWorkbookGeneration: PropTypes.bool,
    }),
    supportingDocumentsS3Key: PropTypes.string,
  }).isRequired,
  reset: PropTypes.bool.isRequired,
  onResetComplete: PropTypes.func.isRequired,
  showDocumentDeleteModal: PropTypes.func.isRequired,
  showRenameFile: PropTypes.func.isRequired,
  showMoveFile: PropTypes.func.isRequired,
  showCopyModal: PropTypes.func.isRequired,
  closeMenu: PropTypes.bool.isRequired,
  oncloseMenuComplete: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  showRollForwardModal: PropTypes.func.isRequired,
};

const DocumentRow = memo(
  ({
    idx,
    rowData,
    onError,
    onSuccess,
    onEditIsOpen,
    editIsOpen,
    onOpen,
    showRollForwardModal,
    refetch,
    availableFolders,
    selectedFolder,
    isPdfReviewAssistant,
  }) => {
    const {rollForwardWalkThru: rollForwardWalkThruEnabled} = useFlags();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {orgConfig, currentUser} = useSelector((state) => state.session);
    const effectiveOrg = useSelector(selectEffectiveOrg);
    const [editDocumentName, setEditDocumentName] = useState(false);
    const [showMoveFile, setShowMoveFile] = useState(false);
    const [documentName, setDocumentName] = useState(
      _.get(rowData, 'name', '')
    );
    const [newDocumentName, setNewDocumentName] = useState(
      _.get(rowData, 'name', '')
    );
    const [documentWorkflowStatus, setDocumentWorkflowStatus] = useState(
      rowData.workflowStatus
    );
    const [resetQuickMenu, setResetQuickMenu] = useState(false);
    const [closeQuickMenu, setCloseQuickMenu] = useState(false);
    const [showDocumentDeleteModal, setShowDocumentDeleteModal] =
      useState(false);
    const [showCopyDocumentModal, setShowCopyDocumentModal] = useState(false);
    const [updateDocument, {isSuccess, isError, error, reset}] =
      useUpdateDocumentMutation();
    const inputRef = useRef(null);

    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setEditDocumentName(false);
      }
    };

    useEffect(() => {
      if (editDocumentName) {
        if (inputRef.current) {
          inputRef.current.querySelector('input').select();
        }
        onEditIsOpen(true);
        document.addEventListener('click', handleClickOutside, true);
        return () => {
          document.removeEventListener('click', handleClickOutside, true);
        };
      } else {
        onEditIsOpen(false);
      }
    }, [editDocumentName]);

    useEffect(() => {
      const decodedName = decode(_.get(rowData, 'name', ''));
      setDocumentName(decodedName);
      setNewDocumentName(decodedName);
      setDocumentWorkflowStatus(rowData.workflowStatus);
    }, [rowData.name, rowData.workflowStatus]);

    useEffect(() => {
      if (isSuccess) {
        setCloseQuickMenu(true);
      }
    }, [isSuccess]);

    useEffect(() => {
      if (isError) {
        setDocumentName(rowData.name);
        setDocumentWorkflowStatus(rowData.workflowStatus);
        setNewDocumentName(rowData.name);
        setResetQuickMenu(true);
        if (_.get(error, 'data.code') === 'DOCUMENT_EXISTS') {
          onError('Filename exists. Please choose another.');
        } else {
          onError();
        }
        reset();
      }
    }, [isError, rowData.name, rowData.workflowStatus, error?.data?.code]);

    const _renderFileType = (line) => {
      // For PDF Review Assistant folders, show "Uploaded PDF"
      if (isPdfReviewAssistant) {
        return 'Uploaded PDF';
      }

      switch (line.type) {
        case 'ANNUAL':
          if (_.has(line, 'fiscalYear')) {
            return `Annual report (${line.fiscalYear})`;
          }
          return 'Annual report';
        case 'QUARTER':
          if (_.has(line, 'fiscalYear') && _.has(line, 'quarter')) {
            return `Quarterly report (${line.quarter} ${line.fiscalYear})`;
          } else if (_.has(line, 'fiscalYear')) {
            return `Quarterly report (${line.fiscalYear})`;
          }
          return 'Quarterly report';
        case 'SEMI_ANNUAL':
          if (_.has(line, 'fiscalYear')) {
            return `Semi-Annual report (${line.fiscalYear})`;
          }
          return 'Semi-Annual report';
        default:
          return 'Basic document';
      }
    };

    const _editDocumentName = (event) => {
      event.stopPropagation();
      setEditDocumentName(!editDocumentName);
      onEditIsOpen(false);
    };

    const _handleDocumentNameChange = (event) => {
      setNewDocumentName(event.target.value);
    };

    const _updateDocumentName = async (event) => {
      const oldName = documentName;
      const trimmedNewName = newDocumentName.trim();

      // Only track and update if the name actually changed
      if (oldName !== trimmedNewName) {
        setDocumentName(trimmedNewName);
        _editDocumentName(event);

        // Track rename event
        trackProductEvent('User Renamed Document', {
          ...getBaseUserProperties(currentUser, effectiveOrg),
          document_id: rowData.documentId,
          document_type: rowData.type,
          old_name: oldName,
          new_name: trimmedNewName,
        });

        await updateDocument({
          documentId: rowData.documentId,
          data: {name: trimmedNewName},
        });
      } else {
        _editDocumentName(event);
      }
    };

    const _handleCellClick = () => {
      if (rowData.ingestionState?.type === DOCUMENT_INGESTION_TYPES.pdfReview) {
        return navigate(`/documents/pdf-review/${rowData.documentId}`);
      }
      if (_.get(rowData, 'status') === 'PENDING') {
        const createFlow =
          rowData.ingestionState.type === 'upload'
            ? CREATE_FLOWS.upload
            : CREATE_FLOWS.rollforward;
        onOpen({
          existingDocumentId: rowData.documentId,
          createFlow,
        });
      } else if (!editDocumentName && !editIsOpen) {
        if (
          rowData?.ingestionState?.type === 'rollforward' &&
          rowData?.ingestionState?.showWalkThrough &&
          rowData?.ingestionState?.source !== 'PDF' &&
          rollForwardWalkThruEnabled
        ) {
          navigate(`/rollforward-walk-through/${rowData.documentId}`, {
            state: {document: rowData},
          });
        } else {
          navigate(`/documents/${rowData.documentId}`);
        }
      }

      dispatch(setDocument({document: rowData}));
    };

    const _handleDeleteDocumentModalSuccess = () => {
      setCloseQuickMenu(true);
      onSuccess('Document deleted successfully');
    };

    const _handleKeyPress = async (event) => {
      switch (event.key) {
        case 'Enter': {
          const oldName = documentName;
          const trimmedNewName = newDocumentName.trim();

          // Only track and update if the name actually changed
          if (oldName !== trimmedNewName) {
            setDocumentName(trimmedNewName);
            setEditDocumentName(false);

            // Track rename event
            trackProductEvent('User Renamed Document', {
              ...getBaseUserProperties(currentUser, effectiveOrg),
              document_id: rowData.documentId,
              document_type: rowData.type,
              old_name: oldName,
              new_name: trimmedNewName,
            });

            await updateDocument({
              documentId: rowData.documentId,
              data: {name: trimmedNewName},
            });
          } else {
            setEditDocumentName(false);
          }
          break;
        }
        case 'Escape':
          setEditDocumentName(false);
          break;
        default:
          break;
      }
    };

    return (
      <React.Fragment>
        <StyledTableRow
          key={idx}
          sx={{cursor: editDocumentName ? 'default' : 'pointer'}}
        >
          <TableCell
            sx={{
              width: TABLE_HEADERS[0].width,
              maxWidth: TABLE_HEADERS[0].maxWidth,
            }}
            onClick={_handleCellClick}
          >
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              <img
                src={
                  isPdfReviewAssistant ? PdfReviewAssistantIcon : DocumentIcon
                }
                alt={''}
              />
              {!editDocumentName && (
                <Typography noWrap sx={styles.table.row.label}>
                  {parse(documentName)}
                </Typography>
              )}
              {editDocumentName && (
                <TextField
                  ref={inputRef}
                  size={'small'}
                  inputRef={(input) => input && input.focus()}
                  sx={styles.table.row.inlineTextField}
                  variant={'standard'}
                  value={newDocumentName}
                  onChange={_handleDocumentNameChange}
                  fullWidth
                  onKeyUp={_handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="confirm name change"
                          onClick={_updateDocumentName}
                          edge="end"
                          size={'small'}
                        >
                          <CheckIcon fontSize={'inherit'} />
                        </IconButton>
                        <IconButton
                          aria-label="confirm name change"
                          onClick={_editDocumentName}
                          edge="end"
                          size={'small'}
                        >
                          <CloseIcon fontSize={'inherit'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Stack>
          </TableCell>
          <TableCell
            sx={{width: TABLE_HEADERS[1].width}}
            onClick={_handleCellClick}
          >
            <Typography sx={styles.table.row.label}>
              {_renderFileType(rowData)}
            </Typography>
          </TableCell>
          <TableCell
            sx={{width: TABLE_HEADERS[2].width}}
            onClick={_handleCellClick}
          >
            <DocumentStatusChip
              rowData={rowData}
              workflowStatus={documentWorkflowStatus}
              orgConfig={orgConfig}
              isPdfReviewAssistant={isPdfReviewAssistant}
            />
          </TableCell>
          <TableCell
            sx={{width: TABLE_HEADERS[3].width}}
            onClick={_handleCellClick}
          >
            <Stack direction={'column'}>
              <Typography sx={styles.table.row.label}>
                <TimeAgo timestamp={rowData.updatedAt} />
              </Typography>
              <Typography sx={styles.table.row.secondaryLabel}>
                By {rowData.updatedByName}
              </Typography>
            </Stack>
          </TableCell>
          <TableCell sx={{width: '4.5rem'}}>
            <QuickActionMenu
              isPdfReviewAssistant={isPdfReviewAssistant}
              rowData={rowData}
              reset={resetQuickMenu}
              onResetComplete={() => setResetQuickMenu(false)}
              showDocumentDeleteModal={() => setShowDocumentDeleteModal(true)}
              showRenameFile={() => setEditDocumentName(true)}
              showMoveFile={() => setShowMoveFile(true)}
              showCopyModal={() => setShowCopyDocumentModal(true)}
              closeMenu={closeQuickMenu}
              oncloseMenuComplete={() => setCloseQuickMenu(false)}
              onOpen={_handleCellClick}
              showRollForwardModal={() =>
                showRollForwardModal(rowData.documentId, rowData.type)()
              }
            />
          </TableCell>
        </StyledTableRow>
        <DeleteDocumentModal
          rowData={rowData}
          open={showDocumentDeleteModal}
          onClose={() => setShowDocumentDeleteModal(false)}
          onSuccess={_handleDeleteDocumentModalSuccess}
          onError={onError}
        />
        {showMoveFile && (
          <MoveFileModal
            selectedFolder={selectedFolder}
            availableFolders={
              rowData.ingestionState?.type ===
              DOCUMENT_INGESTION_TYPES.pdfReview
                ? availableFolders.filter(
                    (f) => f.purpose === FOLDER_PURPOSES.pdfReview
                  )
                : availableFolders.filter(
                    (f) => f.purpose !== FOLDER_PURPOSES.pdfReview
                  )
            }
            open={showMoveFile}
            onClose={() => setShowMoveFile(false)}
            documentId={rowData.documentId}
            documentName={rowData.name}
            onSuccess={(message) => {
              onSuccess(message);
              refetch();
            }}
            onError={(message) => {
              onError(message);
            }}
          />
        )}
        <CopyDocumentModal
          open={showCopyDocumentModal}
          onClose={() => setShowCopyDocumentModal(false)}
          sourceDocument={rowData}
          onSuccess={(message) => {
            onSuccess(message);
            refetch();
          }}
          onError={(message) => {
            onError(message);
          }}
        />
      </React.Fragment>
    );
  }
);

DocumentRow.displayName = 'DocumentRow';
DocumentRow.propTypes = {
  idx: PropTypes.number.isRequired,
  rowData: PropTypes.shape({
    documentId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    updatedByName: PropTypes.string.isRequired,
    ingestionState: PropTypes.shape({
      type: PropTypes.string,
      status: PropTypes.string,
      source: PropTypes.string,
      previousDocumentId: PropTypes.string,
      previousPresentationFormat: PropTypes.string,
      pdfS3Key: PropTypes.string,
      skipWorkbookGeneration: PropTypes.bool,
      startTime: PropTypes.string || PropTypes.instanceOf(Date),
      endTime: PropTypes.string || PropTypes.instanceOf(Date),
      error: PropTypes.object,
      showWalkThrough: PropTypes.bool,
    }),
    type: PropTypes.string.isRequired,
    fiscalYear: PropTypes.string,
    quarter: PropTypes.string,
    status: PropTypes.string,
    workflowStatus: PropTypes.string,
    scheduledLockDate: PropTypes.string,
    isFinal: PropTypes.bool,
  }),
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onEditIsOpen: PropTypes.func.isRequired,
  editIsOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  showRollForwardModal: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  availableFolders: PropTypes.array.isRequired,
  selectedFolder: PropTypes.object.isRequired,
  isPdfReviewAssistant: PropTypes.bool,
};

class DocumentsPage extends AppPage {
  constructor(props) {
    super(props);
    this.state = {
      showCreateReportModal: false,
      createFlow: '',
      documentNameIsDirty: false,
      numDocuments: 0,
      folderName: '',
      documentFoldersEnabled: false,
      folderPurpose: '',
    };
  }

  _onClick = (createFlow) => {
    this.setState({
      createFlow,
      showCreateReportModal: true,
    });
  };

  _showCreateModal = (show = false, createFlow = '') => {
    this.setState({
      createFlow,
      showCreateReportModal: show,
    });
  };

  _setCreateFlow = (createFlow) => {
    this.setState({createFlow});
  };

  _setNumDocuments = (numDocuments) => {
    this.setState({numDocuments});
  };

  _setFolderName = (folderName) => {
    this.setState({folderName});
  };

  _setDocumentFoldersEnabled = (documentFoldersEnabled) => {
    this.setState({documentFoldersEnabled});
  };

  _setFolderPurpose = (folderPurpose) => {
    this.setState({folderPurpose});
  };

  getPageTitle = () => {
    const {folderName} = this.state;
    const selectedFolder = this.props.folders?.selectedFolder;
    const folderYear = selectedFolder?.year;

    if (folderYear && folderName) {
      return `${folderYear} - ${folderName}`;
    } else {
      return folderName;
    }
  };

  renderHeaderContent = () => {
    const {hasParentOrg} = this.props.session;
    return (
      <Box display={'flex'} justifyContent={'center'} width={'100%'} px={7.75}>
        <Box maxWidth={MAX_PAGE_CONTENT_WIDTH} width={'100%'} pt={4}>
          <Stack alignItems="flex-start" spacing={2} pb={2} pt={3.5}>
            {this.state.documentFoldersEnabled ? (
              <Breadcrumbs
                separator={
                  <ChevronRightIcon
                    sx={{color: '#162523', width: 20, height: 20}}
                  />
                }
                aria-label="breadcrumb"
                sx={{mb: 0}}
              >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <IconButton
                    component={Link}
                    to="/folders"
                    sx={{
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ArrowLeft size={20} color="#162523" />
                  </IconButton>
                  <MuiLink
                    component={Link}
                    to="/folders"
                    color="#162523"
                    sx={{
                      textDecoration: 'none',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '150%',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Folders
                  </MuiLink>
                </Box>
              </Breadcrumbs>
            ) : (
              hasParentOrg && (
                <Box>
                  <Breadcrumbs
                    separator={
                      <ChevronRightIcon
                        sx={{color: '#65706D', width: 16, height: 16}}
                      />
                    }
                    aria-label="breadcrumb"
                    sx={{mb: 2}}
                  >
                    <MuiLink
                      component={Link}
                      to="/parent-organization"
                      color="#65706D"
                      sx={{
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Home
                    </MuiLink>
                    <Typography
                      color="#162523"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {this.getPageTitle()}
                    </Typography>
                  </Breadcrumbs>
                </Box>
              )
            )}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography variant="h2" color="#043B34">
                {this.getPageTitle()}
              </Typography>
              {this.renderCustomHeaderContent()}
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  };

  renderBodyContent = () => {
    return (
      <React.Fragment>
        <DocumentsPageComponent
          createFlow={this.state.createFlow}
          showCreateReportModal={this.state.showCreateReportModal}
          showCreateModal={this._showCreateModal}
          setNumDocuments={this._setNumDocuments}
          setFolderName={this._setFolderName}
          setDocumentFoldersEnabled={this._setDocumentFoldersEnabled}
          setFolderPurpose={this._setFolderPurpose}
          accessHelper={this.props.accessHelper}
          documentFoldersEnabled={this.state.documentFoldersEnabled}
        />
      </React.Fragment>
    );
  };

  renderCustomHeaderContent = () => {
    const isPdfReviewAssistant =
      this.state.folderPurpose === FOLDER_PURPOSES.pdfReview;

    if (isPdfReviewAssistant) {
      return (
        <Button
          variant={'contained'}
          startIcon={<AddIcon />}
          sx={styles.createButton}
          onClick={() => this._onClick(CREATE_FLOWS.pdfReviewAssistant)}
        >
          <Typography style={styles.createButtonLabel}>Upload</Typography>
        </Button>
      );
    }

    return (
      <CreateDocumentMenu
        onClick={this._onClick}
        numDocuments={this.state.numDocuments}
      />
    );
  };
}

const DocumentsPageComponent = ({
  createFlow,
  showCreateReportModal,
  showCreateModal,
  setNumDocuments,
  setFolderName,
  setDocumentFoldersEnabled,
  setFolderPurpose,
  accessHelper,
  documentFoldersEnabled: propDocumentFoldersEnabled,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {documentFolders: documentFoldersEnabled} = useFlags();
  const folderPathId = documentFoldersEnabled
    ? location.pathname.split('/')[2]
    : null;
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updatedAt');
  const [sortedTableData, setSortedTableData] = useState([]);
  const [skip, setSkip] = useState(true);
  const {currentOrg, isAuthenticated, orgConfig, isExternalUser} = useSelector(
    (state) => state.session
  );
  const navigate = useNavigate();
  const selectedFolder = useSelector((state) => state.folders.selectedFolder);
  const {id: folderId} = selectedFolder || {};

  // Always fetch folder data when we have a folderPathId (URL is source of truth)
  const {data: folderData, isLoading: isFolderLoading} = useGetFolderQuery(
    {folderId: folderPathId},
    {
      skip:
        !documentFoldersEnabled ||
        !isAuthenticated ||
        !currentOrg?.orgId ||
        !folderPathId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Use the folder ID from URL or Redux, preferring URL as source of truth
  const currentFolderId = folderPathId || folderId;

  // Use folder-specific query when folders are enabled, otherwise use general documents query
  const {
    data: folderDocumentsData,
    isLoading: isFolderDocumentsLoading,
    isError: isFolderDocumentsError,
    isSuccess: isFolderDocumentsSuccess,
    refetch: refetchFolderDocuments,
  } = useGetDocumentsByFolderIdQuery(
    {folderId: currentFolderId},
    {
      skip: !documentFoldersEnabled || !currentFolderId || skip,
      refetchOnMountOrArgChange: true,
      pollingInterval: 30000,
    }
  );

  const {
    data: allDocumentsData,
    isLoading: isAllDocumentsLoading,
    isError: isAllDocumentsError,
    isSuccess: isAllDocumentsSuccess,
    refetch: refetchAllDocuments,
  } = useGetDocumentsQuery(
    {},
    {
      skip, // Always fetch all documents for search purposes
      refetchOnMountOrArgChange: true,
      pollingInterval: 30000,
    }
  );

  const {data: foldersData} = useGetFoldersQuery(
    {},
    {
      skip: !documentFoldersEnabled || !isAuthenticated || !currentOrg?.orgId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Unified data and loading states
  const data = documentFoldersEnabled ? folderDocumentsData : allDocumentsData;
  const isLoading = documentFoldersEnabled
    ? isFolderDocumentsLoading
    : isAllDocumentsLoading;
  const isError = documentFoldersEnabled
    ? isFolderDocumentsError
    : isAllDocumentsError;
  const isSuccess = documentFoldersEnabled
    ? isFolderDocumentsSuccess
    : isAllDocumentsSuccess;
  const refetch = documentFoldersEnabled
    ? refetchFolderDocuments
    : refetchAllDocuments;

  const searchData = useMemo(() => {
    // Filter out documents that are rollforwards but not completed
    const filterDocuments = (documents) => {
      return documents.filter((item) => {
        // If it's a document (not a folder), check ingestion criteria
        if (item.type && item.type !== 'folder') {
          const itemIsRollForward =
            item?.ingestionState?.type === 'rollforward';
          const itemIsUpload = item?.ingestionState?.type === 'upload';
          const ingestionStatus = item?.ingestionState?.status;
          const isCompleted = ingestionStatus === INGESTION_STATUSES.completed;

          // Exclude if it's a rollforward or upload but not completed
          if ((itemIsRollForward || itemIsUpload) && !isCompleted) {
            return false;
          }
        }
        return true;
      });
    };

    if (documentFoldersEnabled) {
      // When folders are enabled, search includes ALL documents and ALL folders
      if (allDocumentsData && foldersData) {
        const filteredData = filterDocuments(allDocumentsData);
        return _.orderBy(
          [...filteredData, ...foldersData],
          [(item) => item.updatedAt || ''],
          ['desc']
        );
      }
    } else {
      // When folders are disabled, search only includes ALL documents
      if (allDocumentsData) {
        const filteredData = filterDocuments(allDocumentsData);
        return _.orderBy(
          filteredData,
          [(item) => item.updatedAt || ''],
          ['desc']
        );
      }
    }
    return [];
  }, [allDocumentsData, foldersData, documentFoldersEnabled]);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editIsOpen, setEditIsOpen] = useState(false);
  const [previousAnnualReports, setPreviousAnnualReports] = useState(null);
  const [previousDocumentId, setPreviousDocumentId] = useState(null);
  const [existingDocumentId, setExistingDocumentId] = useState(null);
  const [sourceDocumentType, setSourceDocumentType] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);

  const {pdfRollForward, uploadWithoutRollforward} = useFlags();

  // Check if this is a PDF Review Assistant folder
  const currentFolder = folderData || selectedFolder;
  const isPdfReviewAssistant =
    currentFolder?.purpose === FOLDER_PURPOSES.pdfReview;

  useEffect(() => {
    if (!orgConfig || Object.keys(orgConfig).length === 0) return;
    initCollabSocket(orgConfig?.collabBaseUrl);
  }, [orgConfig]);

  useEffect(() => {
    setDocumentFoldersEnabled(documentFoldersEnabled);
  }, [documentFoldersEnabled, setDocumentFoldersEnabled]);

  // Sync folder data from API to Redux when available
  // This ensures Redux has data even after a page refresh
  useEffect(() => {
    if (folderData && folderPathId) {
      dispatch(setSelectedFolder({selectedFolder: folderData}));
    }
  }, [folderData, folderPathId, dispatch]);

  // Sync folder purpose to parent component
  useEffect(() => {
    const currentFolder = folderData || selectedFolder;
    setFolderPurpose(currentFolder?.purpose || FOLDER_PURPOSES.regular);
  }, [folderData, selectedFolder, setFolderPurpose]);

  useEffect(() => {
    const currentFolder = folderData || selectedFolder;

    if (foldersData && currentFolder && currentFolder.year) {
      const foldersForThisYear = foldersData.filter(
        (folder) =>
          folder.year === currentFolder.year && folder.id !== currentFolder.id
      );
      setAvailableFolders(foldersForThisYear);
    }
  }, [foldersData, folderData, selectedFolder]);

  useEffect(() => {
    if (!documentFoldersEnabled) {
      setFolderName('Reports');
    } else {
      // Use folder data from API or Redux
      const currentFolder = folderData || selectedFolder;
      if (currentFolder?.name) {
        setFolderName(currentFolder.name);
      } else {
        setFolderName('Folders');
      }
    }
  }, [documentFoldersEnabled, folderData, selectedFolder, setFolderName]);

  useEffect(() => {
    if (!showCreateReportModal) {
      setExistingDocumentId(null);
      setPreviousDocumentId(null);
      setSourceDocumentType(null);
    }
  }, [showCreateReportModal]);

  useEffect(() => {
    setSkip(_.isNil(currentOrg) || _.isEmpty(currentOrg) || !isAuthenticated);
  }, [currentOrg, isAuthenticated]);

  useEffect(() => {
    if (currentOrg && accessHelper) {
      const canEdit = accessHelper.hasPermission(
        currentOrg.orgId,
        'can_edit_reports'
      );
      dispatch(setReadOnly({readOnly: !canEdit}));
    }
  }, [currentOrg, accessHelper, dispatch]);

  useEffect(() => {
    setSortedTableData(sortRecords(data || [], order, orderBy));
    const _sortedAnnualReports = _.orderBy(
      _.filter(
        data,
        (doc) =>
          (doc.type === DOCUMENT_TYPES.annualReport ||
            doc.type === DOCUMENT_TYPES.quarterlyReport ||
            doc.type === DOCUMENT_TYPES.semiAnnualReport) &&
          doc?.ingestionState?.status !== INGESTION_STATUSES.failed
      ),
      ['fiscalYear', 'name'],
      ['desc', 'asc']
    );
    setPreviousAnnualReports(_sortedAnnualReports);
    setNumDocuments(_.size(data));
  }, [data, order, orderBy]);

  const _onShowError = (
    errorMessage = 'There was an error processing your request. Please try again later.'
  ) => {
    setErrorMessage(errorMessage);
    setShowError(true);
  };

  const _onHideError = () => {
    setErrorMessage('');
    setShowError(false);
  };

  const _onShowSuccess = (message = 'Operation completed successfully.') => {
    setSuccessMessage(message);
    setShowSuccess(true);
  };

  const _onHideSuccess = () => {
    setSuccessMessage('');
    setShowSuccess(false);
  };

  const _handleRequestSort = (property) => () => {
    const _isAsc = orderBy === property && order === 'asc';
    setOrder(_isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const _renderError = () => {
    if (!isLoading && isError) {
      return (
        <Error
          hasError={isError}
          message={
            'There was an error processing your request. Please try again later.'
          }
        />
      );
    }
  };

  const _onOpen = ({existingDocumentId, createFlow}) => {
    setExistingDocumentId(existingDocumentId);
    let determinedCreateFlow;
    if (createFlow === CREATE_FLOWS.upload) {
      determinedCreateFlow = CREATE_FLOWS.upload;
    } else if (createFlow === CREATE_FLOWS.rollforward) {
      determinedCreateFlow = pdfRollForward
        ? CREATE_FLOWS.rollforward
        : CREATE_FLOWS.annualRollForward;
    } else {
      determinedCreateFlow = CREATE_FLOWS.annualRollForward;
    }
    showCreateModal(true, determinedCreateFlow);
  };

  const _showRollForwardModal = (documentId, documentType) => () => {
    trackProductEvent('Rollforward Initiated', {
      source_document_id: documentId,
      document_type: documentType,
      source: 'document_context_menu',
    });

    setPreviousDocumentId(documentId);
    setSourceDocumentType(documentType);
    showCreateModal(
      true,
      pdfRollForward ? CREATE_FLOWS.rollforward : CREATE_FLOWS.annualRollForward
    );
  };

  const _renderTable = () => {
    // Wait for folder data to load when viewing a specific folder
    if (
      documentFoldersEnabled &&
      folderPathId &&
      !folderData &&
      isFolderLoading
    ) {
      return null;
    }

    if (
      !isLoading &&
      isSuccess &&
      (sortedTableData.length > 0 || !pdfRollForward)
    ) {
      return (
        <React.Fragment>
          {!isPdfReviewAssistant && (
            <SearchBar
              searchData={searchData || []}
              sx={{marginTop: '0', marginBottom: '1.5rem'}}
            />
          )}
          <Stack direction={'column'} spacing={'1.5rem'}>
            {!isPdfReviewAssistant && (
              <Typography sx={styles.recentDocuments.label}>
                Recent reports
              </Typography>
            )}
            <Stack direction={'column'} spacing={'0.75rem'}>
              <TableContainer sx={styles.table.container}>
                <Table sx={{borderCollapse: 'separate'}}>
                  <TableHead cellSpacing={'2.5rem'}>
                    <TableRow
                      sx={{
                        '& th': {
                          borderTop: '1px solid #DFE5E2',
                          borderBottom: '1px solid #DFE5E2',
                          backgroundColor: '#E4EAE7',
                        },
                        '& th:first-of-type': {
                          borderLeft: '1px solid #DFE5E2',
                          borderRadius: '8px 0 0 8px',
                        },
                        '& th:last-of-type': {
                          borderRight: '1px solid #DFE5E2',
                          borderRadius: '0 8px 8px 0',
                        },
                      }}
                    >
                      {_.map(TABLE_HEADERS, (_header) => (
                        <TableCell
                          key={_header.id}
                          variant={'head'}
                          sortDirection={orderBy === _header.id ? order : false}
                          sx={{
                            ...styles.table.header.cell,
                            width: _header.width,
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === _header.id}
                            direction={orderBy === _header.id ? order : 'asc'}
                            onClick={_handleRequestSort(_header.id)}
                          >
                            <Typography sx={styles.table.header.label}>
                              {_header.label}
                            </Typography>
                            {orderBy === _header.id ? (
                              <Box component={'span'} sx={visuallyHidden}>
                                {order === 'desc'
                                  ? 'sorted descending'
                                  : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{...styles.table.header.cell, width: '4.5rem'}}
                      />
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
              <TableContainer
                sx={{
                  maxHeight: `calc(100vh - ${APP_HEADER_HEIGHT} - ${FOOTER_HEIGHT} - 5.075rem - 2.06rem - 1rem - 1.5rem - 2.28125rem - 0.75rem - 2rem)`,
                }}
              >
                <Table
                  stickyHeader
                  sx={{borderCollapse: 'separate', borderSpacing: '0 0.375rem'}}
                >
                  <TableBody sx={{marginTop: '0.75rem'}}>
                    {_.map(sortedTableData, (_data, _idx) => {
                      return (
                        <DocumentRow
                          key={_idx}
                          idx={_idx}
                          rowData={_data}
                          onError={_onShowError}
                          onSuccess={_onShowSuccess}
                          onEditIsOpen={setEditIsOpen}
                          editIsOpen={editIsOpen}
                          onOpen={_onOpen}
                          showRollForwardModal={_showRollForwardModal}
                          refetch={refetch}
                          availableFolders={availableFolders}
                          selectedFolder={selectedFolder}
                          isPdfReviewAssistant={isPdfReviewAssistant}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        </React.Fragment>
      );
    }
  };

  const _renderEmptyState = () => {
    const readOnly = useSelector((state) => state.documents.readOnly);

    if (
      !isLoading &&
      isSuccess &&
      sortedTableData.length === 0 &&
      pdfRollForward &&
      data &&
      data.length === 0
    ) {
      // PDF Review Assistant empty state
      if (isPdfReviewAssistant) {
        return _renderPdfReviewAssistantEmptyState();
      }

      if (readOnly) {
        return _renderReadOnlyEmptyState();
      }

      // Default empty state
      return _renderCreateFirstReport();
    }
  };

  const _renderPdfReviewAssistantEmptyState = () => {
    return (
      <Stack
        direction={'column'}
        spacing={'1.5rem'}
        alignItems={'center'}
        justifyContent={'center'}
        flex={1}
        sx={{
          backgroundColor: WHITE_100,
          borderRadius: '0.75rem',
          padding: '3rem',
          minHeight: '400px',
          border: '1px solid #E1E5E4',
        }}
      >
        <Box
          sx={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.5rem',
            backgroundColor: '#E8F5F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '0.5rem',
          }}
        >
          <img src={PdfReviewAssistantGreenIcon} alt="PDF review assistant" />
        </Box>
        <Stack
          direction="column"
          spacing="0.5rem"
          justifyContent="center"
          alignItems="center"
        >
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: '600',
              lineHeight: '120%',
              color: DARK_TEXT_COLOR,
            }}
          >
            PDF review assistant
          </Typography>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: '400',
              lineHeight: '150%',
              color: '#67716E',
              textAlign: 'center',
            }}
          >
            AI Annotation for PDF files, upload to get started.
          </Typography>
        </Stack>
        <Button
          variant={'contained'}
          startIcon={<AddIcon />}
          sx={{...styles.createButton, marginTop: '0.5rem'}}
          onClick={() => showCreateModal(true, CREATE_FLOWS.pdfReviewAssistant)}
        >
          <Typography style={styles.createButtonLabel}>Upload</Typography>
        </Button>
      </Stack>
    );
  };

  const _renderReadOnlyEmptyState = () => {
    return (
      <Stack
        direction={'column'}
        spacing={'1.25rem'}
        alignItems={'center'}
        justifyContent={'center'}
        flex={1}
      >
        <Typography sx={styles.createReport.header}>
          Nothing here yet
        </Typography>
        <Box width={'35.3125rem'}>
          <Typography sx={styles.createReport.secondaryLabel}>
            You don&apos;t have permission to create reports, but you&apos;ll be
            able to view them once they&apos;re available.
          </Typography>
        </Box>
      </Stack>
    );
  };

  const _renderCreateFirstReport = () => {
    return (
      <Stack
        direction={'column'}
        spacing={'1.25rem'}
        alignItems={'center'}
        justifyContent={'center'}
        flex={1}
      >
        <Typography sx={styles.createReport.header}>
          Create your first report!
        </Typography>
        <Box width={'35.3125rem'}>
          <Typography sx={styles.createReport.secondaryLabel}>
            To get started, you can rollforward a previous report or start with
            a blank file.
          </Typography>
        </Box>
        <Stack direction={'row'} spacing={'1rem'} paddingTop={'1.75rem'}>
          <Button
            sx={styles.createReport.button.container}
            onClick={() => showCreateModal(true, CREATE_FLOWS.blankDocument)}
          >
            <Stack direction={'row'} spacing={'1rem'} alignItems={'center'}>
              <Box sx={styles.createReport.button.icon.container}>
                <Plus style={styles.createReport.button.icon.image} />
              </Box>
              <Stack
                direction={'column'}
                spacing={'0.125rem'}
                alignItems={'flex-start'}
              >
                <Typography sx={styles.createReport.button.label}>
                  Create blank report
                </Typography>
                <Typography sx={styles.createReport.button.secondaryLabel}>
                  Start with a blank file
                </Typography>
              </Stack>
            </Stack>
          </Button>
          <Button
            sx={styles.createReport.button.container}
            onClick={() => showCreateModal(true, CREATE_FLOWS.pdf)}
          >
            <Stack direction={'row'} spacing={'1rem'} alignItems={'center'}>
              <Box sx={styles.createReport.button.icon.container}>
                <Upload style={styles.createReport.button.icon.image} />
              </Box>
              <Stack
                direction={'column'}
                spacing={'0.125rem'}
                alignItems={'flex-start'}
              >
                <Typography sx={styles.createReport.button.label}>
                  Rollforward a PDF file
                </Typography>
                <Typography sx={styles.createReport.button.secondaryLabel}>
                  Start with an existing report
                </Typography>
              </Stack>
            </Stack>
          </Button>
          {uploadWithoutRollforward && (
            <Button
              sx={styles.createReport.button.container}
              onClick={() => showCreateModal(true, CREATE_FLOWS.upload)}
            >
              <Stack direction={'row'} spacing={'1rem'} alignItems={'center'}>
                <Box sx={styles.createReport.button.icon.container}>
                  <Upload style={styles.createReport.button.icon.image} />
                </Box>
                <Stack
                  direction={'column'}
                  spacing={'0.125rem'}
                  alignItems={'flex-start'}
                >
                  <Typography sx={styles.createReport.button.label}>
                    Upload PDF
                  </Typography>
                  <Typography sx={styles.createReport.button.secondaryLabel}>
                    Upload a PDF file
                  </Typography>
                </Stack>
              </Stack>
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const _onRollForwardComplete = () => {
    refetch();
  };

  const _onDocumentCreated = () => {
    refetch();
  };

  // Filter previous documents based on source document type
  const filteredPreviousDocuments = sourceDocumentType
    ? _.filter(previousAnnualReports, {type: sourceDocumentType})
    : previousAnnualReports;

  useEffect(() => {
    if (isExternalUser) {
      navigate('/');
    }
  }, [isExternalUser]);

  return (
    <CommonPageWrapper
      height={'100%'}
      data-component={'DocumentsPageComponent'}
      data-file-source={'DocumentsPage.jsx'}
    >
      <Stack
        direction={'row'}
        flex={1}
        height={'100%'}
        paddingBottom={'2.5rem'}
      >
        <Stack direction={'column'} flex={1}>
          <Loading
            loading={
              isLoading ||
              (isFolderLoading && documentFoldersEnabled && folderPathId)
            }
          />
          {_renderError()}
          {_renderTable()}
          {_renderEmptyState()}
        </Stack>
      </Stack>
      {pdfRollForward && (
        <CreateDocumentModal
          open={showCreateReportModal}
          onClose={showCreateModal}
          onError={_onShowError}
          previousDocuments={filteredPreviousDocuments}
          previousDocumentId={previousDocumentId}
          existingDocumentId={existingDocumentId}
          createFlow={createFlow}
          sourceDocumentType={sourceDocumentType}
          onRollForwardComplete={_onRollForwardComplete}
          onDocumentCreated={_onDocumentCreated}
        />
      )}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={_onHideError}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        sx={{top: '54px !important'}}
      >
        <Notification
          status="error"
          title="Something went wrong"
          subtitle={errorMessage}
          onClose={_onHideError}
        />
      </Snackbar>
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={_onHideSuccess}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        sx={{top: '54px !important'}}
      >
        <Notification status="success" title={successMessage} />
      </Snackbar>
    </CommonPageWrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    session: state.session,
    folders: state.folders,
  };
};

DocumentsPageComponent.propTypes = {
  createFlow: PropTypes.string.isRequired,
  showCreateReportModal: PropTypes.bool.isRequired,
  showCreateModal: PropTypes.func.isRequired,
  setNumDocuments: PropTypes.func.isRequired,
  setFolderName: PropTypes.func.isRequired,
  setDocumentFoldersEnabled: PropTypes.func.isRequired,
  setFolderPurpose: PropTypes.func.isRequired,
  accessHelper: PropTypes.object,
  documentFoldersEnabled: PropTypes.bool,
};

export default withLDConsumer()(
  connect(mapStateToProps)(withRequiredAuthInfo(DocumentsPage))
);
